import { EmployeeListItemMinDto } from 'types/employees';
import {
    CalculatedPricelist,
    EmployeeRate,
    HourlyRate,
    PersonalRate,
    PricelistLabelType,
    PricelistPeriod,
    PricelistPeriodWrite,
    PricelistRateRow,
    PricingKind,
    RepricedStarts,
    StagedPricelistPeriod,
} from 'types/pricelists';
import { foldForSearch } from 'utils/foldForSearch';

// A colleague the grid can price: everyone active today, plus whoever a stored period already prices.
export interface PricedPerson {
    id: number;
    name: string;
    jobRoleId: number | null;
    jobRoleName: string | null;
    isActive: boolean;
    leftOn: string | null;
}

// One period while it is edited. Prices are keyed by employee id; an empty string is no price, which bills the
// same as no row at all, so it is never sent.
export interface PeriodDraft {
    key: string;
    id: number | null;
    validFrom: string | null;
    prices: Record<number, string>;
}

export type PeriodDateError = 'required' | 'duplicate' | 'out-of-order';

export interface RoleGroup {
    key: string;
    jobRoleName: string | null;
    people: PricedPerson[];
}

export const PRICE_PATTERN = /^[0-9]*[.,]?([0-9]{0,2})?$/;

export function isEmployeePriced(list: { pricingKind?: PricingKind | null } | null | undefined): boolean {
    return list?.pricingKind === PricingKind.EMPLOYEE;
}

// The kind a list opens its editor in: its stored kind, or for a new list the tenant's mode.
export function editorPricingKind(
    list: { pricingKind?: PricingKind | null } | null | undefined,
    isNew: boolean,
    isEmployeePricingEnabled: boolean
): PricingKind {
    if (!isNew) {
        return list?.pricingKind ?? PricingKind.ROLE;
    }
    return isEmployeePricingEnabled ? PricingKind.EMPLOYEE : PricingKind.ROLE;
}

// An employee-priced list has no validity window, so "only currently valid" keeps it whatever its labels say.
export function isCurrentlyValid(pricelist: CalculatedPricelist): boolean {
    return isEmployeePriced(pricelist) || (pricelist.labelsList?.includes(PricelistLabelType.VALID) ?? false);
}

// The undated first period first, then by date. Dates are ISO days, so they compare as strings.
export function sortPeriods<T extends { validFrom: string | null }>(periods: T[]): T[] {
    return periods.toSorted((a, b) => {
        if (a.validFrom === b.validFrom) return 0;
        if (a.validFrom === null) return -1;
        if (b.validFrom === null) return 1;
        return a.validFrom < b.validFrom ? -1 : 1;
    });
}

function pricesOf(rates: EmployeeRate[]): Record<number, string> {
    return Object.fromEntries(
        rates
            .filter((rate) => rate.hourlyPrice !== null && rate.hourlyPrice !== undefined && rate.hourlyPrice !== '')
            .map((rate) => [rate.employee.id, String(rate.hourlyPrice)])
    );
}

// The stored or staged periods as editable drafts; a list without any gets its undated first period.
export function periodsFromList(periods: StagedPricelistPeriod[] | null | undefined): PeriodDraft[] {
    if (!periods?.length) {
        return [{ key: 'first', id: null, validFrom: null, prices: {} }];
    }
    return sortPeriods(periods).map((period, index) => ({
        key: period.id === null ? `new-${index}` : `period-${period.id}`,
        id: period.id,
        validFrom: period.validFrom,
        prices: pricesOf(period.rates),
    }));
}

// The drafts in the shape a form stages them in, each row named, so the form's card can show them unsaved.
export function stagedFromDrafts(periods: PeriodDraft[], people: Map<number, PricedPerson>): StagedPricelistPeriod[] {
    return periods.map((period) => ({
        id: period.id,
        validFrom: period.validFrom,
        rates: draftRowsAsRates(period, people),
    }));
}

// A copy of another list's periods to start a new list from: the same dates and prices, none of its ids.
export function copiedPeriods(periods: PricelistPeriod[] | null | undefined): StagedPricelistPeriod[] {
    return sortPeriods(periods ?? []).map((period) => ({ id: null, validFrom: period.validFrom, rates: period.rates }));
}

export function writesFromStaged(periods: StagedPricelistPeriod[]): PricelistPeriodWrite[] {
    return toPeriodWrites(periodsFromList(periods));
}

// The period in force on `today`: the latest one that has started, the first period when none has.
export function inForcePeriodOf<T extends { validFrom: string | null; inForce?: boolean }>(
    periods: T[] | null | undefined,
    today: string
): T | undefined {
    if (!periods?.length) return undefined;
    const flagged = periods.find((period) => period.inForce);
    if (flagged) return flagged;
    return sortPeriods(periods).reduce<T>(
        (current, period) => (period.validFrom === null || period.validFrom <= today ? period : current),
        sortPeriods(periods)[0]
    );
}

// Everyone active from the colleague list, and the people stored rows name who are not in it any more. The
// colleague list knows the role held today; a stored row is the fallback for someone who has left.
export function buildPeople(
    activeEmployees: EmployeeListItemMinDto[],
    periods: StagedPricelistPeriod[] | null | undefined
): Map<number, PricedPerson> {
    const people = new Map<number, PricedPerson>();
    for (const employee of activeEmployees) {
        people.set(employee.id, {
            id: employee.id,
            name: employee.name,
            jobRoleId: employee.latestEmployment?.jobRoleId ?? null,
            jobRoleName: employee.latestEmployment?.jobRoleName ?? null,
            isActive: true,
            leftOn: null,
        });
    }
    for (const period of periods ?? []) {
        for (const rate of period.rates) {
            if (people.has(rate.employee.id)) continue;
            people.set(rate.employee.id, {
                id: rate.employee.id,
                name: rate.employee.name,
                jobRoleId: rate.jobRoleId,
                jobRoleName: rate.jobRoleName,
                isActive: rate.employeeActive && !rate.employeeDeletedAt,
                leftOn: rate.employeeLeftOn,
            });
        }
    }
    return people;
}

// The rows of one period: every active colleague and anyone who has left but still has a price here, matched by
// name or role, grouped by the role held today. Groups follow the job roles' own order where it is known.
export function groupPeopleByRole(
    people: Map<number, PricedPerson>,
    period: PeriodDraft,
    search: string,
    roleOrder: Map<number, number> = new Map()
): RoleGroup[] {
    const query = foldForSearch(search.trim());
    const shown = [...people.values()].filter((person) => {
        if (!person.isActive && !period.prices[person.id]) return false;
        if (!query) return true;
        return foldForSearch(person.name).includes(query) || foldForSearch(person.jobRoleName ?? '').includes(query);
    });
    const groups = new Map<string, RoleGroup>();
    for (const person of shown) {
        const key = person.jobRoleId === null ? 'no-role' : `role-${person.jobRoleId}`;
        const group = groups.get(key) ?? { key, jobRoleName: person.jobRoleName, people: [] };
        group.people.push(person);
        groups.set(key, group);
    }
    const rank = (group: RoleGroup): number => {
        const roleId = group.people[0].jobRoleId;
        return roleId === null ? Infinity : (roleOrder.get(roleId) ?? Number.MAX_SAFE_INTEGER);
    };
    return [...groups.values()]
        .map((group) => ({ ...group, people: group.people.toSorted((a, b) => a.name.localeCompare(b.name)) }))
        .toSorted((a, b) => rank(a) - rank(b) || (a.jobRoleName ?? '').localeCompare(b.jobRoleName ?? ''));
}

export function nextPeriodKey(periods: PeriodDraft[]): string {
    let index = periods.length;
    while (periods.some((period) => period.key === `new-${index}`)) index += 1;
    return `new-${index}`;
}

// The period a new one dated `validFrom` starts as a copy of, as the server copies it: the one before its date.
function periodBefore(periods: PeriodDraft[], validFrom: string): PeriodDraft | undefined {
    return inForcePeriodOf(periods, validFrom);
}

// A new period, on any date, past included, starts as a copy of the period before it and takes its place by date.
export function addPeriod(periods: PeriodDraft[], validFrom: string): { periods: PeriodDraft[]; key: string } {
    const key = nextPeriodKey(periods);
    const added: PeriodDraft = { key, id: null, validFrom, prices: { ...periodBefore(periods, validFrom)?.prices } };
    return { periods: sortPeriods([...periods, added]), key };
}

/**
 * A period with its new date. A period added in this edit whose prices are still the copy it started with is copied
 * again from the period before its new date; a stored one, or one already edited, keeps its prices.
 */
export function redatePeriod(periods: PeriodDraft[], period: PeriodDraft, validFrom: string | null): PeriodDraft {
    const others = periods.filter((other) => other.key !== period.key);
    const copiedFrom = period.validFrom === null ? undefined : periodBefore(others, period.validFrom);
    const isUntouchedCopy =
        period.id === null && copiedFrom !== undefined && samePrices(period.prices, copiedFrom.prices);
    if (!isUntouchedCopy || validFrom === null) {
        return { ...period, validFrom };
    }
    return { ...period, validFrom, prices: { ...periodBefore(others, validFrom)?.prices } };
}

export function isFutureDate(validFrom: string | null, today: string): boolean {
    return validFrom !== null && validFrom > today;
}

// A stored period can be removed only while it has not started; a period added in this edit always can.
export function canRemovePeriod(period: PeriodDraft, today: string): boolean {
    if (period.validFrom === null) return false;
    return period.id === null || isFutureDate(period.validFrom, today);
}

// The strip keeps the order the periods had when it opened, so a date can move but not past a neighbour.
export function validatePeriodDates(periods: PeriodDraft[]): Record<string, PeriodDateError> {
    const errors: Record<string, PeriodDateError> = {};
    periods.forEach((period, index) => {
        if (index === 0) return;
        if (!period.validFrom) {
            errors[period.key] = 'required';
            return;
        }
        if (periods.some((other) => other.key !== period.key && other.validFrom === period.validFrom)) {
            errors[period.key] = 'duplicate';
            return;
        }
        const previous = periods[index - 1].validFrom;
        const next = periods[index + 1]?.validFrom;
        if ((previous && period.validFrom <= previous) || (next && period.validFrom >= next)) {
            errors[period.key] = 'out-of-order';
        }
    });
    return errors;
}

function normalizePrice(price: string): string {
    return String(Number(price.replace(',', '.')));
}

export function setPrice(period: PeriodDraft, employeeId: number, value: string): PeriodDraft {
    const prices = { ...period.prices };
    if (value === '') {
        delete prices[employeeId];
    } else {
        prices[employeeId] = value.replace(',', '.');
    }
    return { ...period, prices };
}

// The full set of periods a save carries, every period with its complete rows: a priced person per row.
export function toPeriodWrites(periods: PeriodDraft[]): PricelistPeriodWrite[] {
    return periods.map((period) => ({
        id: period.id,
        validFrom: period.validFrom,
        rates: Object.entries(period.prices)
            .filter(([, price]) => price !== '')
            .map(([employeeId, price]) => ({
                employee: { id: Number(employeeId) },
                hourlyPrice: normalizePrice(price),
            })),
    }));
}

function samePrices(a: Record<number, string>, b: Record<number, string>): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((key) => {
        const left = a[Number(key)];
        const right = b[Number(key)];
        if (!left || !right) return !left && !right;
        return Number(left) === Number(right);
    });
}

// Where a save re-prices work already done: each started date of a period added, re-dated or repriced on today or
// earlier, or of a started period moved or removed, since the work from its old date then takes the preceding period's
// prices; and the undated first period, which prices everything before the first dated one, so it always has started.
export function repricedPastStarts(drafts: PeriodDraft[], stored: PeriodDraft[], today: string): RepricedStarts | null {
    const changed = drafts.flatMap((draft) => {
        const original = stored.find((period) => period.id !== null && period.id === draft.id);
        if (!original) return [draft.validFrom];
        if (original.validFrom === draft.validFrom && samePrices(original.prices, draft.prices)) return [];
        return [draft.validFrom, original.validFrom];
    });
    const removed = stored
        .filter((period) => period.id !== null && !drafts.some((draft) => draft.id === period.id))
        .map((period) => period.validFrom);
    const starts = [...changed, ...removed];
    const fromStart = starts.includes(null);
    const validFrom = [
        ...new Set(starts.filter((start): start is string => start !== null && start <= today)),
    ].toSorted();
    return fromStart || validFrom.length ? { validFrom, fromStart } : null;
}

// The priced rows of a period, highest price first, as the rate breakdown and the clipboard show them.
export function pricedEmployeeRows(rates: EmployeeRate[] | undefined): EmployeeRate[] {
    return (rates ?? [])
        .filter((rate) => rate.hourlyPrice !== null && rate.hourlyPrice !== undefined && rate.hourlyPrice !== '')
        .toSorted(
            (a, b) => Number(b.hourlyPrice) - Number(a.hourlyPrice) || a.employee.name.localeCompare(b.employee.name)
        );
}

export function employeeRowName(rate: Pick<EmployeeRate, 'employee' | 'jobRoleName'>): string {
    return rate.jobRoleName ? `${rate.employee.name} · ${rate.jobRoleName}` : rate.employee.name;
}

// A draft period's rows in the read shape, for the cards that show a list before it is saved.
export function draftRowsAsRates(period: PeriodDraft | undefined, people: Map<number, PricedPerson>): EmployeeRate[] {
    if (!period) return [];
    return Object.entries(period.prices).map(([employeeId, price]) => {
        const person = people.get(Number(employeeId));
        return {
            employee: { id: Number(employeeId), name: person?.name ?? '' },
            jobRoleId: person?.jobRoleId ?? null,
            jobRoleName: person?.jobRoleName ?? null,
            employeeActive: person?.isActive ?? true,
            employeeLeftOn: person?.leftOn ?? null,
            employeeDeletedAt: null,
            hourlyPrice: price,
        };
    });
}

// The min–max of a period's priced people: every priced person counts, there is no role to exclude.
export function employeePriceRange(rates: EmployeeRate[]): { min: number; max: number } | null {
    const prices = pricedEmployeeRows(rates).map((rate) => Number(rate.hourlyPrice));
    return prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : null;
}

// A case list's person rows in the catalogue's shape, highest price first, for the cards that show people. The role
// is the one held today, which is all a case list tells.
export function employeeRatesOfCaseList(personalRates: PersonalRate[] | undefined): EmployeeRate[] {
    return pricedEmployeeRows(
        (personalRates ?? []).map((row) => ({
            employee: row.employee,
            jobRoleId: row.jobRole?.id ?? null,
            jobRoleName: row.jobRole ? row.jobRole.internalName || row.jobRole.name : null,
            employeeActive: true,
            employeeLeftOn: null,
            employeeDeletedAt: null,
            hourlyPrice: row.hourlyPrice,
        }))
    );
}

export function roleRatesOf(rows: PricelistRateRow[]): HourlyRate[] {
    return rows.filter((row): row is HourlyRate => row.jobRole !== null);
}

// The people of a rates read in the catalogue's shape, highest price first; the read names no role for them.
export function employeeRatesOfRateRows(rows: PricelistRateRow[]): EmployeeRate[] {
    return pricedEmployeeRows(
        rows.flatMap((row) =>
            row.employee
                ? [
                      {
                          employee: row.employee,
                          jobRoleId: null,
                          jobRoleName: null,
                          employeeActive: true,
                          employeeLeftOn: null,
                          employeeDeletedAt: null,
                          hourlyPrice: row.hourlyPrice,
                      },
                  ]
                : []
        )
    );
}
